port module Main exposing (..)

import Browser
import Browser.Dom as Dom
import Css
import Group exposing (Group, decodeGroup, viewGroup)
import Html.Styled as Html exposing (Html, button, div, h1, img, node, text, toUnstyled)
import Html.Styled.Attributes as Html exposing (class, src)
import Html.Styled.Events exposing (onClick)
import Json.Decode as Decode
import Json.Encode as Encode
import List
import List.Extra
import String
import Task
import Variable exposing (FilledGroup, FilledVariable)


type alias Flags =
    { title : String
    , groups : Decode.Value
    , displayedVariables : List String
    , displayedGroups : List String
    }


type GenerationState
    = Generated
    | Generating
    | GenerationError


type alias Model =
    { title : String
    , allGroups : List Group
    , displayedVariables : List String
    , previousGroups : List Group
    , currentGroup : Maybe Group
    , nextGroups : List Group
    , userFilledGroups : List FilledGroup
    , generationState : GenerationState
    }


type AppState
    = Idle Model
    | DocumentError String


type Msg
    = NoOp
    | Next
    | Previous
    | VariableGroupChanged String String String
    | DocumentGenerated
    | DocumentGenerationError
    | GenerationClicked String
    | SetDisplayedVariables (List String)
    | SetDisplayedGroups (List String)


main : Program Flags AppState Msg
main =
    Browser.element
        { init =
            \flags ->
                let
                    isInDisplayedGroups group =
                        List.member group.id flags.displayedGroups

                    groups =
                        Decode.decodeValue (Decode.list decodeGroup) flags.groups
                            |> Result.withDefault []

                    ( currentGroup, previousGroups, nextGroups ) =
                        case groups of
                            head :: tail ->
                                ( Just head, [], List.filter isInDisplayedGroups tail )

                            [] ->
                                ( Nothing, [], [] )
                in
                ( Idle
                    { title = flags.title
                    , allGroups = groups
                    , displayedVariables = flags.displayedVariables
                    , currentGroup = currentGroup
                    , previousGroups = previousGroups
                    , nextGroups = nextGroups
                    , userFilledGroups = []
                    , generationState = Generated
                    }
                , Cmd.none
                )
        , view = \state -> view state |> toUnstyled
        , update =
            \msg state ->
                case ( msg, state ) of
                    ( _, DocumentError _ ) ->
                        ( state, Cmd.none )

                    ( _, Idle model ) ->
                        update msg model
                            |> (\( newModel, cmd ) -> ( Idle newModel, cmd ))
        , subscriptions = subscriptions
        }


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    case msg of
        NoOp ->
            ( model, Cmd.none )

        Next ->
            let
                ( currentGroup, previousGroups, nextGroups ) =
                    case model.nextGroups of
                        head :: tail ->
                            ( Just head, Maybe.map (\g -> g :: model.previousGroups) model.currentGroup |> Maybe.withDefault model.previousGroups, tail )

                        [] ->
                            ( Nothing, Maybe.map (\g -> g :: model.previousGroups) model.currentGroup |> Maybe.withDefault model.previousGroups, [] )
            in
            ( { model | currentGroup = currentGroup, previousGroups = previousGroups, nextGroups = nextGroups }
            , jumpToTop "variables-container"
            )

        Previous ->
            let
                ( currentGroup, previousGroups, nextGroups ) =
                    case model.previousGroups of
                        head :: tail ->
                            ( Just head, tail, Maybe.map (\g -> g :: model.nextGroups) model.currentGroup |> Maybe.withDefault model.nextGroups )

                        [] ->
                            ( Nothing, [], Maybe.map (\g -> g :: model.nextGroups) model.currentGroup |> Maybe.withDefault model.nextGroups )
            in
            ( { model | currentGroup = currentGroup, previousGroups = previousGroups, nextGroups = nextGroups }
            , jumpToTop "variables-container"
            )

        VariableGroupChanged groupId variableId value ->
            let
                updatedUserFilledVariables variables =
                    if List.any (\variable -> variable.id == variableId) variables then
                        List.map
                            (\variable ->
                                if variable.id == variableId then
                                    { variable | value = value }

                                else
                                    variable
                            )
                            variables

                    else
                        { id = variableId
                        , value = value
                        }
                            :: variables

                updatedUserFilledGroups =
                    if List.any (\group -> group.id == groupId) model.userFilledGroups then
                        List.map
                            (\group ->
                                if group.id == groupId then
                                    { group | variables = updatedUserFilledVariables group.variables }

                                else
                                    group
                            )
                            model.userFilledGroups

                    else
                        { id = groupId
                        , variables =
                            [ { id = variableId
                              , value = value
                              }
                            ]
                        }
                            :: model.userFilledGroups
            in
            ( { model | userFilledGroups = updatedUserFilledGroups }
            , changeVariableValue
                (Encode.object
                    [ ( "variablesGroups", Encode.list encodeFilledGroup updatedUserFilledGroups )
                    , ( "variableId", Encode.string variableId )
                    , ( "groupId", Encode.string groupId )
                    ]
                )
            )

        DocumentGenerated ->
            ( { model | generationState = Generated }, Cmd.none )

        DocumentGenerationError ->
            ( { model | generationState = GenerationError }, Cmd.none )

        GenerationClicked docType ->
            ( { model | generationState = Generating }
            , callGenerateDocument
                (Encode.object
                    [ ( "documentId", Encode.string "title" )
                    , ( "variablesGroups", Encode.list encodeFilledGroup model.userFilledGroups )
                    , ( "format", Encode.string docType )
                    ]
                )
            )

        SetDisplayedVariables displayedVariables ->
            ( { model | displayedVariables = displayedVariables }, Cmd.none )

        SetDisplayedGroups displayedGroups ->
            let
                currentGroupIndex =
                    case model.currentGroup of
                        Just value ->
                            List.Extra.elemIndex value model.allGroups |> Maybe.withDefault 0

                        Nothing ->
                            0

                allPreviousGroups =
                    List.take currentGroupIndex model.allGroups

                allNextGroups =
                    List.drop (currentGroupIndex + 1) model.allGroups

                isInDisplayedGroups group =
                    List.member group.id displayedGroups

                previousGroups =
                    List.filter isInDisplayedGroups allPreviousGroups

                nextGroups =
                    List.filter isInDisplayedGroups allNextGroups
            in
            ( { model | previousGroups = previousGroups, nextGroups = nextGroups }, Cmd.none )


jumpToTop : String -> Cmd Msg
jumpToTop id =
    Dom.getViewportOf id
        |> Task.andThen (\_ -> Dom.setViewportOf id 0 0)
        |> Task.attempt (\_ -> NoOp)


view : AppState -> Html Msg
view state =
    case state of
        DocumentError error ->
            text error

        Idle model ->
            viewDocument model


viewDocument : Model -> Html Msg
viewDocument model =
    let
        divide a b =
            a / b

        multiply a b =
            a * b

        countCurrent =
            case model.currentGroup of
                Just group ->
                    1

                Nothing ->
                    0

        progress =
            List.length model.nextGroups
                + List.length model.previousGroups
                + countCurrent
                |> toFloat
                |> divide (List.length model.previousGroups |> toFloat)
                |> multiply 100
    in
    div
        [ class "smartdoc-section" ]
        [ div
            [ Html.id "variables-container", class "variables-container" ]
            [ div
                [ class "variables-header" ]
                [ img [ src "https://global-uploads.webflow.com/5d70bc929b8d7b70bf8ae70b/612e4a63d236ed4651b59d1c_icon_edit_doc_fluo_green.svg" ] []
                , h1 [ class "variables-title" ]
                    [ text model.title ]
                ]
            , div [ class "progress-background" ]
                [ div
                    [ class "progress"
                    , Html.css
                        [ Css.width (Css.pct progress)
                        ]
                    ]
                    []
                , div [ class "progress-text" ] [ text (String.concat [ String.fromInt (round progress), " %" ]) ]
                ]
            , model.currentGroup
                |> Maybe.map (\g -> viewGroup (VariableGroupChanged g.id) g model.userFilledGroups model.displayedVariables)
                |> Maybe.withDefault (text "")
            , if model.currentGroup == Nothing then
                div [ class "generate-buttons" ]
                    [ case model.generationState of
                        Generated ->
                            button
                                [ onClick (GenerationClicked "docx")
                                , class "generate-button word"
                                ]
                                [ text "Télécharger (Word)" ]

                        GenerationError ->
                            button
                                [ onClick (GenerationClicked "docx")
                                , class "generate-button word"
                                ]
                                [ text "Erreur" ]

                        Generating ->
                            button
                                [ onClick NoOp
                                , class "generate-button word"
                                ]
                                [ text "Génération ..." ]
                    , case model.generationState of
                        Generated ->
                            button
                                [ onClick (GenerationClicked "pdf")
                                , class "generate-button pdf"
                                ]
                                [ text "Télécharger (PDF)" ]

                        GenerationError ->
                            button
                                [ onClick (GenerationClicked "pdf")
                                , class "generate-button pdf"
                                ]
                                [ text "Erreur" ]

                        Generating ->
                            button
                                [ onClick NoOp
                                , class "generate-button pdf"
                                ]
                                [ text "Génération ..." ]
                    ]

              else
                div [] []
            , div [ class "nav-buttons" ]
                [ if model.previousGroups /= [] then
                    button
                        [ class "nav-button previous-button"
                        , onClick Previous
                        ]
                        [ text "Retour" ]

                  else
                    div [] []
                , if model.currentGroup /= Nothing then
                    button
                        [ onClick Next
                        , class "nav-button next-button"
                        ]
                        [ text "Suivant" ]

                  else
                    div [] []
                ]
            , div [ class "info-text" ] [ text "Complétez votre document pour le télécharger au format Word ou PDF." ]
            , div [ class "info-text" ] [ text "Vous pourrez ensuite modifier et réutiliser le document Word comme vous le souhaitez." ]
            ]
        , div
            [ class "document-container" ]
            [ div
                [ class "document-preview" ]
                [ div
                    [ class "inner-preview" ]
                    [ node "qiiro-preview"
                        [ Html.property "groups" (encodeFilledGroups model.userFilledGroups) ]
                        []
                    ]
                ]
            ]
        ]



-- ENCODER


encodeFilledGroups : List FilledGroup -> Encode.Value
encodeFilledGroups groups =
    Encode.list encodeFilledGroup groups


encodeFilledGroup : FilledGroup -> Encode.Value
encodeFilledGroup group =
    Encode.object
        [ ( "id", Encode.string group.id )
        , ( "variables", encodeFilledVariables group.variables )
        ]


encodeFilledVariables : List FilledVariable -> Encode.Value
encodeFilledVariables variables =
    Encode.list encodeFilledVariable variables


encodeFilledVariable : FilledVariable -> Encode.Value
encodeFilledVariable variable =
    Encode.object
        [ ( "id", Encode.string variable.id )
        , ( "value", Encode.string variable.value )
        ]


subscriptions : AppState -> Sub Msg
subscriptions appState =
    Sub.batch
        [ documentGenerated (\() -> DocumentGenerated)
        , documentGenerationError (\() -> DocumentGenerationError)
        , changedVariable (\displayedVariables -> SetDisplayedVariables displayedVariables)
        , changedGroup (\displayedGroups -> SetDisplayedGroups displayedGroups)
        ]



--Document generation


port callGenerateDocument : Encode.Value -> Cmd msg


port documentGenerated : (() -> msg) -> Sub msg


port documentGenerationError : (() -> msg) -> Sub msg



-- Update displayed variables on variable change


port changeVariableValue : Encode.Value -> Cmd msg


port changedVariable : (List String -> msg) -> Sub msg


port changedGroup : (List String -> msg) -> Sub msg
